/**
 * Google Calendar Service
 * Manages Google Calendar events for approved reservations via Netlify Functions
 */

// =====================================================
// Configuration
// =====================================================

const NETLIFY_FUNCTIONS_URL =
  import.meta.env.VITE_NETLIFY_FUNCTIONS_URL ||
  'https://ejs-website-redesign.netlify.app/.netlify/functions';

// =====================================================
// Calendar Operations
// =====================================================

/**
 * Add a reservation to Google Calendar
 */
export async function addReservationToCalendar(reservation: {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  reservation_date: string;
  start_time: string;
  end_time: string;
  party_size: number;
  notes: string | null;
  reservation_type: 'back_room' | 'entire_shop';
}): Promise<string> {
  try {
    console.log('[Google Calendar] Creating event via Netlify function');

    const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/google-calendar-add-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(reservation),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to create calendar event');
    }

    const data = await response.json();
    console.log('[Google Calendar] Event created successfully:', data.eventId);
    return data.eventId;
  } catch (error) {
    console.error('[Google Calendar] Error adding reservation to calendar:', error);
    throw error;
  }
}

/**
 * Update a reservation in Google Calendar
 */
export async function updateReservationInCalendar(
  calendarEventId: string,
  reservation: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    reservation_date: string;
    start_time: string;
    end_time: string;
    party_size: number;
    notes: string | null;
    admin_notes?: string | null;
    reservation_type: 'back_room' | 'entire_shop';
  }
): Promise<void> {
  try {
    console.log('[Google Calendar] Updating event via Netlify function:', calendarEventId);

    const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/google-calendar-update-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId: calendarEventId,
        reservation,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to update calendar event');
    }

    console.log('[Google Calendar] Event updated successfully');
  } catch (error) {
    console.error('[Google Calendar] Error updating reservation in calendar:', error);
    throw error;
  }
}

/**
 * Delete a reservation from Google Calendar
 */
export async function deleteReservationFromCalendar(
  calendarEventId: string
): Promise<void> {
  try {
    console.log('[Google Calendar] Deleting event via Netlify function:', calendarEventId);

    const response = await fetch(`${NETLIFY_FUNCTIONS_URL}/google-calendar-delete-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ eventId: calendarEventId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Failed to delete calendar event');
    }

    console.log('[Google Calendar] Event deleted successfully');
  } catch (error) {
    console.error('[Google Calendar] Error deleting reservation from calendar:', error);
    throw error;
  }
}
